import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Seo from '../../components/Seo';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { supabase } from '../../lib/supabaseClient';
import { useAuth } from '../../auth/AuthProvider';

const LabsDetail = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const postId = searchParams?.get('id') || '';
  const { user, role } = useAuth();

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [detail, setDetail] = useState(null);
  const [reply, setReply] = useState(null);

  const [replyText, setReplyText] = useState('');
  const [isSavingReply, setIsSavingReply] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const canEdit = useMemo(() => {
    if (!user?.id || !detail) return false;
    return detail.authorId === user.id || role === 'admin';
  }, [detail, role, user?.id]);

  const load = useCallback(async () => {
    if (!postId) {
      setError('글 ID가 없습니다.');
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const { data, error: err } = await supabase
        .from('ch_labs_posts')
        .select('id, title, author_id, author_name, created_at, content_text, is_published')
        .eq('id', postId)
        .maybeSingle();

      if (err || !data) {
        setError(err?.message || '글을 불러오지 못했습니다.');
        setDetail(null);
        setReply(null);
        return;
      }

      setDetail({
        id: data.id,
        title: data.title,
        authorId: data.author_id,
        authorName: data.author_name || '익명',
        date: data.created_at ? String(data.created_at).slice(0, 10) : '',
        contentText: data.content_text || '',
        isPublished: !!data.is_published,
      });

      const { data: replyRows } = await supabase
        .from('ch_labs_post_replies')
        .select('id, post_id, admin_id, content_text, created_at, updated_at')
        .eq('post_id', postId)
        .order('created_at', { ascending: true })
        .limit(1);

      const r = (replyRows || [])[0] || null;
      setReply(
        r
          ? {
            id: r.id,
            contentText: r.content_text || '',
            date: r.updated_at ? String(r.updated_at).slice(0, 10) : r.created_at ? String(r.created_at).slice(0, 10) : '',
          }
          : null
      );

      setReplyText(r?.content_text || '');
    } finally {
      setIsLoading(false);
    }
  }, [postId]);

  useEffect(() => {
    load();
  }, [load]);

  const ensureLogin = useCallback(() => {
    navigate('/login-registration');
  }, [navigate]);

  const handleDelete = useCallback(async () => {
    if (!canEdit || !detail?.id) return;

    const ok = window.confirm('정말 삭제하시겠습니까?');
    if (!ok) return;

    setIsDeleting(true);
    try {
      const { error: err } = await supabase
        .from('ch_labs_posts')
        .delete()
        .eq('id', detail.id);

      if (err) {
        setError(err?.message || '삭제에 실패했습니다.');
        return;
      }

      navigate('/labs');
    } finally {
      setIsDeleting(false);
    }
  }, [canEdit, detail?.id, navigate]);

  const handleSaveReply = useCallback(async () => {
    if (role !== 'admin') return;
    if (!user?.id) {
      ensureLogin();
      return;
    }

    setIsSavingReply(true);
    setError('');

    try {
      if (reply?.id) {
        const { error: err } = await supabase
          .from('ch_labs_post_replies')
          .update({ content_text: replyText })
          .eq('id', reply.id);

        if (err) {
          setError(err?.message || '답변 저장에 실패했습니다.');
          return;
        }
      } else {
        const { error: err } = await supabase
          .from('ch_labs_post_replies')
          .insert({
            post_id: postId,
            admin_id: user.id,
            content_text: replyText,
          });

        if (err) {
          setError(err?.message || '답변 등록에 실패했습니다.');
          return;
        }
      }

      await load();
    } finally {
      setIsSavingReply(false);
    }
  }, [ensureLogin, load, postId, reply?.id, replyText, role, user?.id]);

  return (
    <div className="min-h-screen bg-background">
      <Seo title={detail?.title ? detail.title : '꿈현 Labs'} canonicalPath="/labs-detail" />

      <header className="sticky top-0 z-navigation bg-card/90 backdrop-blur-sm border-b border-border">
        <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 h-16 flex items-center justify-between">
          <button type="button" onClick={() => navigate(-1)} className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-gaming">
            <Icon name="ArrowLeft" size={18} />
            <span className="text-sm font-medium">뒤로</span>
          </button>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => navigate('/labs-editor')}
            >
              글 등록
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-screen-2xl mx-auto px-4 sm:px-5 lg:px-8 py-5 md:py-8 lg:py-12">
        {isLoading ? (
          <div className="bg-card rounded-lg shadow-gaming-md p-6 border border-border">
            <p className="text-sm text-muted-foreground caption">로딩 중...</p>
          </div>
        ) : error ? (
          <div className="bg-card rounded-lg shadow-gaming-md p-6 border border-border">
            <p className="text-sm text-destructive">{error}</p>
          </div>
        ) : detail ? (
          <div className="bg-card rounded-lg shadow-gaming-md p-4 md:p-6 lg:p-8 border border-border">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground">{detail.title}</h1>
                <div className="mt-2 text-xs md:text-sm text-muted-foreground caption">{detail.date} · {detail.authorName}</div>
              </div>

              <div className="flex items-center gap-2">
                {canEdit ? (
                  <>
                    <Button
                      variant="outline"
                      onClick={() => navigate(`/labs-editor?id=${detail.id}`)}
                    >
                      수정
                    </Button>
                    <Button
                      variant="destructive"
                      onClick={handleDelete}
                      loading={isDeleting}
                    >
                      삭제
                    </Button>
                  </>
                ) : null}
              </div>
            </div>

            <div className="mt-6 whitespace-pre-line text-sm md:text-base text-foreground">{detail.contentText}</div>

            <div className="mt-8 pt-6 border-t border-border">
              <h2 className="text-base md:text-lg font-heading font-semibold text-foreground">관리자 답변</h2>

              {reply ? (
                <div className="mt-3 rounded-md border border-border bg-muted/30 p-4">
                  <div className="text-xs text-muted-foreground caption">{reply.date} · 꿈현공장장</div>
                  <div className="mt-2 whitespace-pre-line text-sm text-foreground">{reply.contentText}</div>
                </div>
              ) : (
                <div className="mt-3 rounded-md border border-border bg-muted/30 p-4">
                  <div className="text-sm text-muted-foreground caption">아직 답변이 없습니다.</div>
                </div>
              )}

              {role === 'admin' ? (
                <div className="mt-4">
                  <label className="text-sm font-heading font-semibold text-foreground block mb-2">답변 등록/수정</label>
                  <textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e?.target?.value)}
                    rows={5}
                    maxLength={2000}
                    className="w-full px-4 py-3 bg-background border border-border rounded-md text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-gaming resize-none"
                    placeholder="관리자 답변을 입력하세요"
                  />
                  <div className="mt-3 flex items-center justify-end">
                    <Button
                      variant="default"
                      onClick={handleSaveReply}
                      loading={isSavingReply}
                      disabled={!replyText?.trim()}
                    >
                      저장
                    </Button>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
      </main>
    </div>
  );
};

export default LabsDetail;
